/**
 * Workflow Validation Utilities
 * Validates workflow structure, connections, and configurations
 */

// Validate the overall workflow structure
export const validateWorkflow = (workflow) => {
  const errors = [];
  const warnings = [];

  // Basic workflow validation
  if (!workflow.name || workflow.name.trim() === '') {
    errors.push({
      type: 'error',
      field: 'name',
      message: 'Workflow name is required'
    });
  }

  // Allow empty workflows for initial creation
  if (!workflow.nodes) {
    workflow.nodes = [];
  }
  
  // Only require nodes if workflow is being activated
  if (workflow.status === 'active' && workflow.nodes.length === 0) {
    errors.push({
      type: 'error',
      field: 'nodes',
      message: 'Active workflow must have at least one node'
    });
  }

  // Validate nodes
  if (workflow.nodes) {
    const nodeValidation = validateNodes(workflow.nodes);
    errors.push(...nodeValidation.errors);
    warnings.push(...nodeValidation.warnings);
  }

  // Validate edges
  if (workflow.edges) {
    const edgeValidation = validateEdges(workflow.edges, workflow.nodes);
    errors.push(...edgeValidation.errors);
    warnings.push(...edgeValidation.warnings);
  }

  // Validate workflow graph structure
  const graphValidation = validateGraphStructure(workflow.nodes, workflow.edges);
  errors.push(...graphValidation.errors);
  warnings.push(...graphValidation.warnings);

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

// Validate individual nodes
export const validateNodes = (nodes) => {
  const errors = [];
  const warnings = [];
  const nodeIds = new Set();

  nodes.forEach((node, index) => {
    // Check for unique IDs
    if (!node.id) {
      errors.push({
        type: 'error',
        nodeId: `node_${index}`,
        message: `Node at index ${index} is missing an ID`
      });
    } else if (nodeIds.has(node.id)) {
      errors.push({
        type: 'error',
        nodeId: node.id,
        message: `Duplicate node ID: ${node.id}`
      });
    }
    nodeIds.add(node.id);

    // Check node type
    if (!node.type) {
      errors.push({
        type: 'error',
        nodeId: node.id,
        message: `Node ${node.id} is missing a type`
      });
    }

    // Validate node position
    if (!node.position || typeof node.position.x !== 'number' || typeof node.position.y !== 'number') {
      warnings.push({
        type: 'warning',
        nodeId: node.id,
        message: `Node ${node.id} has invalid position data`
      });
    }

    // Type-specific validation
    const typeValidation = validateNodeByType(node);
    if (typeValidation.errors) errors.push(...typeValidation.errors);
    if (typeValidation.warnings) warnings.push(...typeValidation.warnings);
  });

  return { errors, warnings };
};

// Validate node based on its type
const validateNodeByType = (node) => {
  const errors = [];
  const warnings = [];

  switch (node.type) {
    case 'trigger':
      // Check for either 'type' or 'triggerType' for backward compatibility
      if (!node.data?.config?.type && !node.data?.config?.triggerType) {
        errors.push({
          type: 'error',
          nodeId: node.id,
          message: 'Trigger node must specify a trigger type'
        });
      }
      break;

    case 'agent':
      if (!node.data?.config?.agentId && !node.data?.config?.agentType) {
        warnings.push({
          type: 'warning',
          nodeId: node.id,
          message: 'Agent node should specify an agent or agent type'
        });
      }
      break;

    case 'condition':
      if (!node.data?.config?.expression && !node.data?.config?.conditions) {
        errors.push({
          type: 'error',
          nodeId: node.id,
          message: 'Condition node must have an expression or conditions'
        });
      }
      break;

    case 'wait':
      if (!node.data?.config?.duration) {
        errors.push({
          type: 'error',
          nodeId: node.id,
          message: 'Wait node must specify a duration'
        });
      }
      break;

    case 'email':
    case 'sms':
      if (!node.data?.config?.templateId && !node.data?.config?.content) {
        warnings.push({
          type: 'warning',
          nodeId: node.id,
          message: `${node.type} node should have a template or content`
        });
      }
      break;

    case 'integration':
      if (!node.data?.config?.integration) {
        errors.push({
          type: 'error',
          nodeId: node.id,
          message: 'Integration node must specify which integration to use'
        });
      }
      break;
  }

  return { errors, warnings };
};

// Validate edges
export const validateEdges = (edges, nodes) => {
  const errors = [];
  const warnings = [];
  const nodeIds = new Set(nodes.map(n => n.id));

  edges.forEach((edge, index) => {
    // Check edge has required fields
    if (!edge.source || !edge.target) {
      errors.push({
        type: 'error',
        edgeId: edge.id || `edge_${index}`,
        message: `Edge at index ${index} is missing source or target`
      });
      return;
    }

    // Check source and target exist
    if (!nodeIds.has(edge.source)) {
      errors.push({
        type: 'error',
        edgeId: edge.id,
        message: `Edge source node '${edge.source}' does not exist`
      });
    }
    if (!nodeIds.has(edge.target)) {
      errors.push({
        type: 'error',
        edgeId: edge.id,
        message: `Edge target node '${edge.target}' does not exist`
      });
    }

    // Check for self-loops
    if (edge.source === edge.target) {
      warnings.push({
        type: 'warning',
        edgeId: edge.id,
        message: `Edge creates a self-loop on node ${edge.source}`
      });
    }
  });

  return { errors, warnings };
};

// Validate graph structure
export const validateGraphStructure = (nodes, edges = []) => {
  const errors = [];
  const warnings = [];

  if (!nodes || nodes.length === 0) {
    return { errors, warnings };
  }

  // Find entry points (nodes with no incoming edges)
  const targetNodes = new Set(edges.map(e => e.target));
  const entryNodes = nodes.filter(n => !targetNodes.has(n.id));

  // Check for trigger nodes
  const triggerNodes = nodes.filter(n => n.type === 'trigger');
  
  if (triggerNodes.length === 0 && entryNodes.length > 0) {
    warnings.push({
      type: 'warning',
      message: 'Workflow has no trigger node. Consider adding one for clarity.'
    });
  }

  if (triggerNodes.length > 1) {
    warnings.push({
      type: 'warning',
      message: 'Workflow has multiple trigger nodes. This may cause confusion.'
    });
  }

  // Check for disconnected nodes
  const connectedNodes = new Set();
  const sourceNodes = new Set(edges.map(e => e.source));
  
  // Add all nodes that are either source or target of an edge
  edges.forEach(edge => {
    connectedNodes.add(edge.source);
    connectedNodes.add(edge.target);
  });

  // Find disconnected nodes
  const disconnectedNodes = nodes.filter(n => 
    !connectedNodes.has(n.id) && nodes.length > 1
  );

  disconnectedNodes.forEach(node => {
    errors.push({
      type: 'error',
      nodeId: node.id,
      message: `Node '${node.data?.label || node.id}' is not connected to the workflow`
    });
  });

  // Check for dead ends (non-terminal nodes with no outgoing edges)
  const terminalTypes = ['email', 'sms', 'action', 'integration'];
  const deadEndNodes = nodes.filter(node => 
    !sourceNodes.has(node.id) && 
    !terminalTypes.includes(node.type) &&
    connectedNodes.has(node.id)
  );

  deadEndNodes.forEach(node => {
    warnings.push({
      type: 'warning',
      nodeId: node.id,
      message: `Node '${node.data?.label || node.id}' has no outgoing connections`
    });
  });

  // Check for cycles
  const cycles = detectCycles(nodes, edges);
  if (cycles.length > 0) {
    warnings.push({
      type: 'warning',
      message: `Workflow contains ${cycles.length} cycle(s). This may cause infinite loops.`,
      details: cycles
    });
  }

  return { errors, warnings };
};

// Detect cycles in the workflow graph
const detectCycles = (nodes, edges) => {
  const adjacencyList = {};
  const cycles = [];

  // Build adjacency list
  nodes.forEach(node => {
    adjacencyList[node.id] = [];
  });
  edges.forEach(edge => {
    if (adjacencyList[edge.source]) {
      adjacencyList[edge.source].push(edge.target);
    }
  });

  // DFS to detect cycles
  const visited = new Set();
  const recursionStack = new Set();
  const path = [];

  const dfs = (nodeId) => {
    visited.add(nodeId);
    recursionStack.add(nodeId);
    path.push(nodeId);

    const neighbors = adjacencyList[nodeId] || [];
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        if (dfs(neighbor)) return true;
      } else if (recursionStack.has(neighbor)) {
        // Found a cycle
        const cycleStart = path.indexOf(neighbor);
        const cycle = path.slice(cycleStart);
        cycle.push(neighbor); // Complete the cycle
        cycles.push(cycle);
        return true;
      }
    }

    recursionStack.delete(nodeId);
    path.pop();
    return false;
  };

  // Check each unvisited node
  Object.keys(adjacencyList).forEach(nodeId => {
    if (!visited.has(nodeId)) {
      dfs(nodeId);
    }
  });

  return cycles;
};

// Validate workflow can be executed
export const validateForExecution = (workflow) => {
  const validation = validateWorkflow(workflow);
  
  // Additional execution-specific checks
  if (workflow.status !== 'active') {
    validation.errors.push({
      type: 'error',
      field: 'status',
      message: 'Workflow must be active to execute'
    });
  }

  // Check for at least one executable path
  const hasExecutablePath = checkExecutablePath(workflow.nodes, workflow.edges);
  if (!hasExecutablePath) {
    validation.errors.push({
      type: 'error',
      message: 'Workflow has no executable path from start to end'
    });
  }

  return validation;
};

// Check if there's at least one path through the workflow
const checkExecutablePath = (nodes, edges) => {
  if (!nodes || nodes.length === 0) return false;
  if (nodes.length === 1) return true;

  // Find entry points
  const targetNodes = new Set(edges.map(e => e.target));
  const entryNodes = nodes.filter(n => !targetNodes.has(n.id));

  if (entryNodes.length === 0) {
    // All nodes have incoming edges - might be a cycle
    return edges.length > 0;
  }

  // Check if at least one entry node has a path
  const adjacencyList = {};
  nodes.forEach(node => {
    adjacencyList[node.id] = [];
  });
  edges.forEach(edge => {
    if (adjacencyList[edge.source]) {
      adjacencyList[edge.source].push(edge.target);
    }
  });

  // DFS from each entry node
  for (const entryNode of entryNodes) {
    const visited = new Set();
    const stack = [entryNode.id];

    while (stack.length > 0) {
      const current = stack.pop();
      if (visited.has(current)) continue;
      visited.add(current);

      const neighbors = adjacencyList[current] || [];
      stack.push(...neighbors);
    }

    // If we visited more than just the entry node, we have a path
    if (visited.size > 1) return true;
  }

  return false;
};

// Helper function to format validation results
export const formatValidationResults = (validation) => {
  const messages = [];

  if (validation.errors.length > 0) {
    messages.push('Errors:');
    validation.errors.forEach(error => {
      messages.push(`  • ${error.message}`);
    });
  }

  if (validation.warnings.length > 0) {
    if (messages.length > 0) messages.push('');
    messages.push('Warnings:');
    validation.warnings.forEach(warning => {
      messages.push(`  • ${warning.message}`);
    });
  }

  return messages.join('\n');
};